import type { OddballParser } from '../parser.js';

// Web Bluetooth API types — not included in TS DOM lib.
declare global {
  interface Navigator {
    bluetooth?: Bluetooth;
  }
  interface Bluetooth {
    requestDevice(options: BluetoothRequestDeviceOptions): Promise<BluetoothDevice>;
  }
  interface BluetoothRequestDeviceOptions {
    filters?: Array<{ services?: string[]; name?: string; namePrefix?: string }>;
    optionalServices?: string[];
  }
  interface BluetoothDevice {
    name: string | null;
    gatt?: BluetoothRemoteGATTServer;
    addEventListener(type: string, listener: EventListener): void;
    removeEventListener(type: string, listener: EventListener): void;
  }
  interface BluetoothRemoteGATTServer {
    connected: boolean;
    connect(): Promise<BluetoothRemoteGATTServer>;
    disconnect(): void;
    getPrimaryService(service: string): Promise<BluetoothRemoteGATTService>;
  }
  interface BluetoothRemoteGATTService {
    getCharacteristic(characteristic: string): Promise<BluetoothRemoteGATTCharacteristic>;
  }
  interface BluetoothRemoteGATTCharacteristic {
    value: DataView | null;
    startNotifications(): Promise<BluetoothRemoteGATTCharacteristic>;
    stopNotifications(): Promise<BluetoothRemoteGATTCharacteristic>;
    addEventListener(type: string, listener: EventListener): void;
    removeEventListener(type: string, listener: EventListener): void;
  }
}

const BLE_MIDI_SERVICE = '03b80e5a-ede8-4b33-a751-6ce34ec4c700';
const BLE_MIDI_CHARACTERISTIC = '7772e5db-3868-4112-a1a9-f2669d106bf3';

/** Number of data bytes expected after a MIDI status byte. */
function midiDataLength(status: number): number {
  const cmd = status & 0xf0;
  if (cmd >= 0x80 && cmd <= 0xb0) return 2; // Note Off, Note On, Poly Aftertouch, CC
  if (cmd === 0xc0 || cmd === 0xd0) return 1; // Program Change, Channel Aftertouch
  if (cmd === 0xe0) return 2; // Pitch Bend
  return 0; // System messages — skip
}

/**
 * Parse a BLE MIDI packet into an array of 3-byte MIDI messages.
 *
 * BLE MIDI packet format:
 * - Byte 0: Header (MSB=1, bits 5-0 = upper timestamp)
 * - Repeating: Timestamp byte (MSB=1), optional status byte (MSB=1), data bytes (MSB=0)
 */
export function parseBlePacket(data: DataView): Uint8Array[] {
  const messages: Uint8Array[] = [];
  const len = data.byteLength;
  if (len < 3) return messages;

  // Validate header
  const header = data.getUint8(0);
  if ((header & 0x80) === 0) return messages;

  let i = 1; // skip header byte
  let runningStatus = 0;

  while (i < len) {
    const byte = data.getUint8(i);

    if (byte & 0x80) {
      // Timestamp byte (MSB=1) — skip it
      i++;
      if (i >= len) break;

      // Check if followed by a new status byte (MSB=1) or running-status data (MSB=0)
      const next = data.getUint8(i);
      if (next & 0x80) {
        runningStatus = next;
        i++;
      }
    }
    // else: running-status continuation without timestamp — data starts at i

    if (runningStatus === 0) break; // No status yet, can't parse

    const dataLen = midiDataLength(runningStatus);
    if (dataLen === 0) {
      // System message — skip until next timestamp byte
      while (i < len && (data.getUint8(i) & 0x80) === 0) i++;
      continue;
    }

    if (i + dataLen > len) break; // Not enough data

    const d1 = data.getUint8(i);
    i++;
    const d2 = dataLen === 2 && i < len ? data.getUint8(i) : 0;
    if (dataLen === 2) i++;

    messages.push(new Uint8Array([runningStatus, d1, d2]));
  }

  return messages;
}

export interface WebBluetoothAdapterOptions {
  /** Called when connection status changes */
  onStatusChange?: (message: string, state: 'connected' | 'disconnected' | 'error') => void;
}

export class WebBluetoothAdapter {
  private parser: OddballParser;
  private options: WebBluetoothAdapterOptions;
  private device: BluetoothDevice | null = null;
  private characteristic: BluetoothRemoteGATTCharacteristic | null = null;
  private onNotification: ((e: Event) => void) | null = null;
  private onDisconnected: ((e: Event) => void) | null = null;

  constructor(parser: OddballParser, options: WebBluetoothAdapterOptions = {}) {
    this.parser = parser;
    this.options = options;
  }

  async connect(): Promise<boolean> {
    if (typeof navigator === 'undefined' || !navigator.bluetooth) {
      this.notify('Web Bluetooth not supported', 'error');
      return false;
    }

    try {
      this.device = await navigator.bluetooth.requestDevice({
        filters: [{ services: [BLE_MIDI_SERVICE] }],
      });
    } catch (err) {
      const msg = (err as Error).message;
      // User cancelled the picker — not an error state
      if (msg?.includes('cancelled') || msg?.includes('canceled')) {
        this.notify('Bluetooth pairing cancelled', 'disconnected');
      } else {
        this.notify(`Bluetooth request failed: ${msg}`, 'error');
      }
      return false;
    }

    try {
      const server = await this.device!.gatt!.connect();
      const service = await server.getPrimaryService(BLE_MIDI_SERVICE);
      this.characteristic = await service.getCharacteristic(BLE_MIDI_CHARACTERISTIC);

      this.onNotification = (e: Event) => {
        const char = e.target as unknown as BluetoothRemoteGATTCharacteristic;
        if (!char.value) return;
        const messages = parseBlePacket(char.value);
        for (const msg of messages) {
          this.parser.feed(msg);
        }
      };
      this.characteristic.addEventListener('characteristicvaluechanged', this.onNotification);
      await this.characteristic.startNotifications();

      this.onDisconnected = () => {
        this.cleanup();
        this.notify('Bluetooth disconnected', 'disconnected');
      };
      this.device!.addEventListener('gattserverdisconnected', this.onDisconnected);

      this.notify(`Connected: ${this.device!.name ?? 'BLE MIDI'}`, 'connected');
      return true;
    } catch (err) {
      this.notify(`Bluetooth connection failed: ${(err as Error).message}`, 'error');
      return false;
    }
  }

  disconnect(): void {
    this.cleanup();
    if (this.device?.gatt?.connected) {
      this.device.gatt.disconnect();
    }
    this.notify('Disconnected', 'disconnected');
  }

  private cleanup(): void {
    if (this.characteristic && this.onNotification) {
      this.characteristic.removeEventListener('characteristicvaluechanged', this.onNotification);
    }
    if (this.device && this.onDisconnected) {
      this.device.removeEventListener('gattserverdisconnected', this.onDisconnected);
    }
    this.characteristic = null;
    this.onNotification = null;
    this.onDisconnected = null;
  }

  private notify(message: string, state: 'connected' | 'disconnected' | 'error'): void {
    this.options.onStatusChange?.(message, state);
  }
}
